/* =============================================================================
   The ONE media player.

   There were two: Plyr for video, and a hand-rolled `new Audio()` with its own
   transport, its own seek bar and its own time formatting for the document's
   audio bar. Two engines means two sets of bugs, two places to fix a keyboard
   shortcut, and two answers to "what happens when the source changes".

   This is the only one. It plays:

     · a file we host          — audio or video, from the media volume
     · an unlisted YouTube id  — the same controls, the same styling

   …because the class recordings are self-hosted today and move to YouTube later,
   and that has to be a change of DATA, not a second component.

   Everything visual comes from `--plyr-*` in index.css, which read the platform
   tokens. Nothing is styled here.
   ============================================================================= */
import { useEffect, useRef } from "react";
import Plyr from "plyr";

/** Where the bytes come from. The only thing a caller has to decide. */
export type MediaSource =
  | { kind: "file"; url: string; mime?: string | null; media: "audio" | "video" }
  | { kind: "youtube"; videoId: string };

export interface MediaPlayerHandle {
  play(): void;
  pause(): void;
  toggle(): void;
  seek(seconds: number): void;
}

/** A class recording is watched, scrubbed and sped up. Video gets the lot. */
/**
 * What a video's chrome is allowed to contain.
 *
 * `pip` and `airplay` are gone. On a 390px-wide bar every control competes for
 * the same row, and the two that lose are the ones that can shrink: the seek
 * bar collapsed to nothing while the VOLUME slider kept its width, so the only
 * slider on screen was the one that does not scrub. Picture-in-picture and
 * AirPlay are a browser convenience, not something this player owes anyone —
 * both are in the browser's own media menu — and dropping them buys the seek
 * bar its room back.
 */
const VIDEO_CONTROLS = [
  "play-large",
  "play",
  "progress",
  "current-time",
  "duration",
  "mute",
  "volume",
  "settings",
  "fullscreen",
];

/** An audio bar has no picture to make big or full-screen. */
/**
 * Does this device have a pointer that can hover? Decided once: it drives
 * whether Plyr draws tooltips at all, and a touch screen never un-hovers.
 */
const HAS_POINTER =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(hover: hover)").matches
    : true;

const AUDIO_CONTROLS = [
  "play",
  "progress",
  "current-time",
  "duration",
  "mute",
  "volume",
  "settings",
];

export function MediaPlayer({
  source,
  poster,
  title,
  autoPlay = false,
  downloadUrl,
  onEnded,
  onReady,
}: {
  source: MediaSource;
  poster?: string | null;
  title?: string;
  autoPlay?: boolean;
  /**
   * When set, the player grows a download control of its own. Plyr has one;
   * a button next to the player would be a second answer to the same
   * question and would miss fullscreen entirely.
   */
  downloadUrl?: string | null;
  /** Used by a queue to advance. */
  onEnded?: () => void;
  onReady?: (handle: MediaPlayerHandle) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Callbacks live in refs so changing one does not tear down and rebuild the
  // player — which, mid-playback, is heard as a gap.
  const endedRef = useRef(onEnded);
  endedRef.current = onEnded;
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  const isAudio = source.kind === "file" && source.media === "audio";
  const key = source.kind === "file" ? source.url : source.videoId;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let el: HTMLElement;
    if (source.kind === "youtube") {
      el = document.createElement("div");
      el.className = "plyr__video-embed";
      const iframe = document.createElement("iframe");
      const params = new URLSearchParams({
        origin: window.location.origin,
        iv_load_policy: "3",
        modestbranding: "1",
        playsinline: "1",
        rel: "0",
      });
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(source.videoId)}?${params}`;
      iframe.allowFullscreen = true;
      iframe.allow = "accelerometer; encrypted-media; gyroscope; picture-in-picture";
      iframe.setAttribute("title", title ?? "Video");
      el.appendChild(iframe);
    } else {
      const media = document.createElement(source.media);
      media.setAttribute("playsinline", "");
      media.preload = "metadata";
      if (!isAudio && poster) (media as HTMLVideoElement).poster = poster;
      const src = document.createElement("source");
      src.src = source.url;
      if (source.mime) src.type = source.mime;
      media.appendChild(src);
      el = media;
    }
    host.appendChild(el);

    const player = new Plyr(el, {
      controls: [
        ...(isAudio ? AUDIO_CONTROLS : VIDEO_CONTROLS),
        ...(downloadUrl ? ['download'] : []),
      ],
      ...(downloadUrl ? { urls: { download: downloadUrl } } : {}),
      settings: ["speed"],
      speed: { selected: 1, options: [0.75, 1, 1.25, 1.5, 1.75, 2] },
      // Ten seconds is about one mantra — the useful unit for re-hearing a line.
      seekTime: 10,
      keyboard: { focused: true, global: false },
      // Tooltips are for a pointer over a video's chrome. The audio player
      // lives in a bar docked to the bottom of the window, where a tooltip has
      // nowhere to go but over the page — and on a touch screen it does not
      // even need a hover to appear: the control keeps focus after a tap, so a
      // "Play" label sat above the bar and stayed there.
      // Tooltips are for a POINTER. The audio player lives in a bar docked to
      // the bottom of the window, where a tooltip has nowhere to go but over
      // the page; and on a touch screen no control ever "un-hovers", so after a
      // tap a "Play" label sat over the video and stayed there. They are for
      // video, on a device that actually has a pointer.
      tooltips: { controls: !isAudio && HAS_POINTER, seek: !isAudio && HAS_POINTER },
      hideControls: !isAudio,
      autoplay: autoPlay,
      ...(isAudio ? {} : { ratio: "16:9" }),
      storage: { enabled: true, key: "vu-plyr" },
      ...(poster && !isAudio ? { poster } : {}),
    });

    const onEnd = () => endedRef.current?.();
    player.on("ended", onEnd);
    readyRef.current?.({
      play: () => void player.play(),
      pause: () => player.pause(),
      toggle: () => void player.togglePlay(),
      seek: (s: number) => {
        player.currentTime = s;
      },
    });

    return () => {
      // Plyr attaches document-level keyboard and fullscreen listeners, so a
      // leaked instance leaves a second player answering the first one's keys.
      try {
        player.off("ended", onEnd);
        player.destroy();
      } catch {
        /* already gone with the node */
      }
      host.replaceChildren();
    };
    // A changed source rebuilds deliberately: Plyr cannot swap between a file
    // and an embed provider in place.
  }, [source.kind, key, isAudio, poster, title, autoPlay, downloadUrl]);

  return (
    <div
      className={`vu-player${isAudio ? " is-audio" : ""}`}
      ref={hostRef}
      data-provider={source.kind}
    />
  );
}
